import { ValidationError } from '~/errors';
import { validateId } from '~/repositories/utils';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  BankAccountConnectType,
  BankAccountConnectionsDto,
  BankAccountSweepDetailsDto,
  BankAccountSweepDto,
  BankAccountSweepDtoPaginationDto,
  BankAccountSweepLimitsDto,
  ConnectBankAccountDto,
  ConnectBankAccountResultDto,
  CreateBankAccountDto,
  HydratedBankAccountDto,
  StripePayoutDetailsDto,
  StripePayoutDto,
  UpdateBankAccountDto,
} from '~/swagger/portal/Api';

const BANK_ACCOUNTS_URL = '/api/bank-accounts';

export type BankAccountSweepsFilter = {
  bankAccountId: string;
  page?: number;
  pageSize?: number;
  'Created.Start'?: string;
  'Created.End'?: string;
};

export function validateBankAccount(bankAccount: HydratedBankAccountDto | CreateBankAccountDto | UpdateBankAccountDto): void {
  if (!bankAccount) throw new ValidationError('BankAccount object must be provided');
}

export class BankAccountRepository extends BaseRestRepository {
  public async getBankAccounts(): Promise<HydratedBankAccountDto[]> {
    const url = BANK_ACCOUNTS_URL;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getBankAccount(bankAccountId: string): Promise<HydratedBankAccountDto> {
    validateId(bankAccountId);
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}`;
    const response = await this.fetchWithAuth(url);

    return this.getJsonResponse(response);
  }

  public async createBankAccount(bankAccount: CreateBankAccountDto): Promise<HydratedBankAccountDto> {
    validateBankAccount(bankAccount);
    const url = BANK_ACCOUNTS_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(bankAccount),
    });
    return this.getJsonResponse(response);
  }

  public async updateBankAccount(bankAccountId: string, bankAccount: UpdateBankAccountDto): Promise<HydratedBankAccountDto> {
    validateId(bankAccountId);
    validateBankAccount(bankAccount);
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(bankAccount),
    });
    return this.getJsonResponse(response);
  }

  public async deleteBankAccount(bankAccountId: string): Promise<void> {
    validateId(bankAccountId);
    if (!bankAccountId) throw new ValidationError('BankAccount ID must be provided');
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    if (!response.ok) {
      console.error(await response.json());
    }
  }

  public async getBankAccountConnections(bankAccountId: string): Promise<BankAccountConnectionsDto> {
    validateId(bankAccountId);
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}/connections`;
    const response = await this.fetchWithAuth(url);

    return this.getJsonResponse(response);
  }

  public async bankAccountConnections(
    params: {
      bankAccountId: string;
      type: BankAccountConnectType;
    } & ConnectBankAccountDto
  ): Promise<ConnectBankAccountResultDto> {
    const { bankAccountId, type, ...connectParams } = params;
    validateId(bankAccountId);
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}/connections/${type}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(connectParams),
    });
    return this.getJsonResponse(response);
  }

  public async plaidSweeps(bankAccountId: string): Promise<BankAccountSweepDto> {
    validateId(bankAccountId);
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}/sweeps`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
    });
    return this.getJsonResponse(response);
  }

  public async getSweepsLimits(): Promise<BankAccountSweepLimitsDto> {
    const url = `${BANK_ACCOUNTS_URL}/sweeps/limits`;
    const response = await this.fetchWithAuth(url);

    return this.getJsonResponse(response);
  }

  public async getBankAccountSweeps(filter: BankAccountSweepsFilter): Promise<BankAccountSweepDtoPaginationDto> {
    const { bankAccountId, ...sweepsFilter } = filter;
    validateId(bankAccountId);
    const url = urlWithQuery(`${BANK_ACCOUNTS_URL}/${bankAccountId}/sweeps`, sweepsFilter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getBankAccountSweepDetails(bankAccountId: string, sweepId: string): Promise<BankAccountSweepDetailsDto> {
    validateId(bankAccountId);
    validateId(sweepId, 'Sweep');
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}/sweeps/${sweepId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getBankAccountPayouts(bankAccountId: string): Promise<StripePayoutDto[]> {
    validateId(bankAccountId);
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}/payouts`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getBankAccountPayout(bankAccountId: string, payoutId: string): Promise<StripePayoutDetailsDto> {
    validateId(bankAccountId);
    validateId(payoutId, 'Payout');
    const url = `${BANK_ACCOUNTS_URL}/${bankAccountId}/payouts/${payoutId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }
}

export const bankAccountRepository = new BankAccountRepository();
